import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { loadSavedJobs } from 'redux/modules/savedJobs';
import { loadPartners } from 'redux/modules/partners';
import JobListing from 'components/JobListing/JobListing';
import { Col, Grid, Row } from 'react-bootstrap';
import JobsBoardBanner from 'containers/Ads/JobsBoardBanner/JobsBoardBanner';
import ApplicationWriterUpsell from 'components/ApplicationWriterUpsell/ApplicationWriterUpsell';
import UpsellSignUp from 'components/Upsell/UpsellSignUp';
import useAuth from 'hooks/useAuth';
import LoadingBubbles from 'components/LoadingBubbles/LoadingBubbles';
import styles from './SavedJobs.module.scss';
import { trackEvent } from 'helpers/Analytics';

const SavedJobs = ({ location }) => {
  const dispatch = useDispatch();
  const {
    savedJobs: initialSavedJobs,
    loading,
    loaded,
    error,
  } = useSelector((state) => state.savedJobs);
  const { partners, loadedPartners, loadingPartners } = useSelector(
    (state) => state.partners
  );
  const { user } = useAuth();

  const [savedJobs, setSavedJobs] = useState([]);

  const testingPartners = location.query.testing ?? false;

  useEffect(() => {
    trackEvent('user-landed-on-my-saved-jobs-page', {
      userId: user ? user.id : 'Guest',
    });
    dispatch(loadSavedJobs());
    dispatch(loadPartners('jobs_board', testingPartners));
  }, []);

  useEffect(() => {
    if (initialSavedJobs?.length) {
      setSavedJobs(initialSavedJobs);
    } else {
      setSavedJobs([]);
    }
  }, [initialSavedJobs]);

  const handleUnsaveJob = (jobId) => {
    setSavedJobs(savedJobs.filter((job) => job.id !== jobId));
  };

  const showUpsell = !user || (user && user?.pro === false);
  let jobsBoardBannerCount = 0;
  const maxBannerCount = 5;

  return (
    <div className="mt++ lato-regular">
      <Grid>
        <Row>
          <Col md={9}>
            <h1 className={styles.title}>My Saved Jobs</h1>
          </Col>
        </Row>
        <Row>
          <Col sm={8} md={9}>
            {loading && (
              <div className="mt++">
                <LoadingBubbles size="large" />
              </div>
            )}
            {loaded && !loading && savedJobs.length > 0 && (
              <>
                {savedJobs.map((savedJob, index) => {
                  const briefListing = (
                    <JobListing
                      brief={savedJob}
                      key={savedJob.id}
                      onUnsave={handleUnsaveJob}
                    />
                  );

                  const shouldShowBanner =
                    (index === 1 || (index + 1) % 15 === 0) &&
                    jobsBoardBannerCount < maxBannerCount;
                  const shouldShowSignUpUpsell = index === 4 && showUpsell;
                  const shouldShowApplicationWriterUpsell = index === 9;

                  if (shouldShowBanner) {
                    jobsBoardBannerCount++;
                  }

                  if (shouldShowBanner) {
                    return (
                      <Fragment key={`job-${savedJob.id}-with-banner`}>
                        {briefListing}
                        <JobsBoardBanner
                          partners={partners}
                          loadedPartners={loadedPartners}
                          loadingPartners={loadingPartners}
                        />
                      </Fragment>
                    );
                  } else if (shouldShowSignUpUpsell) {
                    return (
                      <Fragment key={`job-${savedJob.id}-with-signup`}>
                        {briefListing}
                        <UpsellSignUp user={user} mobile={true} />
                      </Fragment>
                    );
                  } else if (shouldShowApplicationWriterUpsell) {
                    return (
                      <Fragment key={`job-${savedJob.id}-with-app-writer`}>
                        {briefListing}
                        <ApplicationWriterUpsell user={user} mobile={true} />
                      </Fragment>
                    );
                  } else {
                    return briefListing;
                  }
                })}
              </>
            )}
            {loaded && !loading && !error && savedJobs.length === 0 && (
              <div className="mt++ text-center">
                <p className={styles.subtitle}>
                  You haven&apos;t saved any jobs yet
                </p>
                <p>
                  Browse the jobs list and click the bookmark icon on jobs
                  you&apos;re interested in to save them for later.
                </p>
                <p>
                  You can also save jobs directly from a job&apos;s details
                  page.
                </p>
              </div>
            )}
            {loaded && !loading && error && (
              <div className="mt++ text-center">
                <p className={styles.subtitle}>
                  Unable to load your saved jobs
                </p>
                <p>
                  We&apos;re experiencing some technical difficulties. Please
                  try refreshing the page or come back later.
                </p>
                <p>
                  If this problem persists, please contact our support team.
                </p>
              </div>
            )}
          </Col>
          <Col sm={4} md={3}>
            {showUpsell && <UpsellSignUp user={user} />}
            <ApplicationWriterUpsell user={user} />
          </Col>
        </Row>
      </Grid>
    </div>
  );
};

SavedJobs.propTypes = {
  location: PropTypes.object,
};

export default SavedJobs;
